import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import LandingDarkScope from "@/components/LandingDarkScope";
import MarketingSiteChrome from "@/components/MarketingSiteChrome";
import {
  fetchPublicStatus,
  STATUS_COLORS,
  STATUS_LABELS,
  STATUS_POLL_MS,
} from "@/lib/public-status";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function StatusDot({ status }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.degraded;
  return (
    <span
      className="inline-block w-2 h-2 rounded-full shrink-0"
      style={{ background: color, boxShadow: `0 0 8px ${color}66` }}
      aria-hidden
    />
  );
}

function ComponentRow({ component }) {
  return (
    <div
      className="flex flex-wrap items-start justify-between gap-4 py-4 border-b border-[var(--border-default)] last:border-0"
      data-testid={`status-component-${component.id}`}
    >
      <div className="min-w-[200px] flex-1">
        <div className="font-medium text-[var(--text-primary)]">{component.name}</div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{component.description}</p>
        {component.statusNote ? (
          <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono">{component.statusNote}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider shrink-0">
        <StatusDot status={component.status} />
        <span style={{ color: STATUS_COLORS[component.status] || "var(--text-secondary)" }}>
          {STATUS_LABELS[component.status] || component.status}
        </span>
      </div>
    </div>
  );
}

function maintenanceBadgeLabel(severity) {
  if (severity === "in_progress") return "In progress";
  if (severity === "resolved") return "Completed";
  return "Scheduled";
}

function MaintenanceBlock({ items, empty }) {
  if (!items?.length) {
    if (empty) return null;
    return <p className="text-sm text-[var(--text-tertiary)]">{empty}</p>;
  }
  return (
    <div className="space-y-4">
      {items.map((n) => (
        <div key={n.id} className="landing-card p-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
              {maintenanceBadgeLabel(n.severity)}
            </span>
            <span className="text-xs text-[var(--text-tertiary)]">
              {formatWhen(n.startsAt)}
              {n.endsAt ? ` → ${formatWhen(n.endsAt)}` : ""}
            </span>
          </div>
          <div className="font-medium">{n.title}</div>
          {n.body ? <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{n.body}</p> : null}
        </div>
      ))}
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const body = await fetchPublicStatus();
      setData(body);
      setError(null);
    } catch (err) {
      setError(err?.message || "Unable to load status");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = window.setInterval(load, STATUS_POLL_MS);
    return () => window.clearInterval(t);
  }, []);

  const overall = data?.overall || "unknown";
  const overallColor = STATUS_COLORS[overall] || "var(--text-secondary)";
  const maintenance = data?.maintenance || {};

  return (
    <LandingDarkScope>
      <MarketingSiteChrome contentClassName="max-w-[900px] mx-auto px-4 md:px-8">
        <div className="text-overline">AivoryEMS</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight font-semibold">System status</h1>
          <button
            type="button"
            className="btn-ghost text-sm inline-flex items-center gap-2"
            onClick={load}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
        <p className="text-[var(--text-secondary)] mt-3 max-w-2xl leading-relaxed">
          Real-time availability for the AivoryEMS platform and the services your team relies on.
          Scheduled maintenance and incident updates are published here as they are confirmed.
        </p>

        {error ? (
          <div className="landing-card mt-8 p-6 border border-[#ef4444]/40">
            <div className="font-medium text-[#ef4444]">Status temporarily unavailable</div>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              We could not load the latest information. Please use Refresh or try again in a few minutes.
            </p>
          </div>
        ) : null}

        {!data && !error ? (
          <div className="landing-card mt-8 p-6 text-sm text-[var(--text-tertiary)]">Loading system status…</div>
        ) : null}

        {data ? (
          <>
            <div className="landing-card mt-8 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <StatusDot status={overall} />
                <div className="font-mono text-lg uppercase tracking-wide" style={{ color: overallColor }}>
                  {STATUS_LABELS[overall] || overall}
                </div>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                Last updated {formatWhen(data.checkedAt)}. This page refreshes automatically every 10 minutes.
              </p>
            </div>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-semibold mb-4">Service availability</h2>
              <div className="landing-card p-6 md:px-8">
                {(data.components || []).map((c) => (
                  <ComponentRow key={c.id} component={c} />
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-semibold mb-4">Maintenance & incidents</h2>
              <div className="space-y-8">
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">
                    In progress
                  </h3>
                  <MaintenanceBlock
                    items={maintenance.active}
                    empty="There is no maintenance in progress at this time."
                  />
                </div>
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">
                    Upcoming
                  </h3>
                  <MaintenanceBlock
                    items={maintenance.upcoming}
                    empty="No scheduled maintenance windows are published."
                  />
                </div>
                {maintenance.resolved?.length ? (
                  <div>
                    <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">
                      Recently completed
                    </h3>
                    <MaintenanceBlock items={maintenance.resolved} />
                  </div>
                ) : null}
              </div>
            </section>
          </>
        ) : null}
      </MarketingSiteChrome>
    </LandingDarkScope>
  );
}
